import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInfiniteQueryLPC, useQueryLPC } from 'app/lib/query';
import type { FunctionalUseInfiniteQueryOptions, FunctionalUseQueryOptions, Pagination } from 'app/types/network';
import type { CreatePostDTO, PostComment, PostResponse, UpdatePostDTO } from 'app/types/post';
import { http } from '../lib/axios';
import { loanKeys, postKeys } from './keys';
import { asFormData } from 'app/lib/form';
import { LoanFilterKey, useAllLoans } from './loan';
import { LoanType, type Loan } from 'app/types/loan';

// ─── Queries ─────────────────────────────────────────────────────────

export function usePosts(opts: FunctionalUseInfiniteQueryOptions<Pagination<PostResponse>> = {}) {
  return useInfiniteQueryLPC<Pagination<PostResponse>>({
    queryKey: postKeys.list(),
    queryFn: ({ pageParam }) =>
      http
        .get<Pagination<PostResponse>>('/posts', {
          params: { skip: pageParam ?? 0 },
        })
        .then((res) => res.data),
    ...opts,
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.skip + lastPage.take < lastPage.total ? lastPage.skip + lastPage.take : undefined,
  });
}

/** Past loans that can still receive their single community post. */
export function getLoansWithoutPosts(loans: Loan[]) {
  const loansById = new Map<Loan['id'], Loan>();

  loans.forEach((loan) => {
    if (!loan.posts?.length) loansById.set(loan.id, loan);
  });

  return [...loansById.values()].sort(
    (a, b) => new Date(b.returned ?? b.end).getTime() - new Date(a.returned ?? a.end).getTime(),
  );
}

export function useLoansWithoutPosts(enabled = true) {
  const borrowingQuery = useAllLoans(LoanFilterKey.PastLoans, LoanType.Borrowing, { enabled });

  return {
    data: getLoansWithoutPosts(borrowingQuery.data ?? []),
    isPending: borrowingQuery.isPending,
    isError: borrowingQuery.isError,
    refetch: borrowingQuery.refetch,
  };
}

export function usePost(id?: PostResponse['id'], opts: FunctionalUseQueryOptions<PostResponse | null> = {}) {
  return useQueryLPC<PostResponse | null>({
    queryKey: postKeys.detail(id ?? ''),
    queryFn: () => (!id ? null : http.get<PostResponse>(`/posts/${id}`).then((res) => res.data)),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(id),
  });
}

export function usePostsByLoan(loanId?: string, opts: FunctionalUseQueryOptions<PostResponse[]> = {}) {
  return useQueryLPC<PostResponse[]>({
    queryKey: postKeys.byLoan(loanId ?? ''),
    queryFn: () => (!loanId ? [] : http.get<PostResponse[]>(`/posts/loan/${loanId}`).then((res) => res.data)),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(loanId),
  });
}

export function usePostsByListing(listingId?: string, opts: FunctionalUseQueryOptions<PostResponse[]> = {}) {
  return useQueryLPC<PostResponse[]>({
    queryKey: postKeys.byListing(listingId ?? ''),
    queryFn: () => (!listingId ? [] : http.get<PostResponse[]>(`/posts/listing/${listingId}`).then((res) => res.data)),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(listingId),
  });
}

export function usePostsByUser(userId?: string, opts: FunctionalUseQueryOptions<PostResponse[]> = {}) {
  return useQueryLPC<PostResponse[]>({
    queryKey: postKeys.byUser(userId ?? ''),
    queryFn: () => (!userId ? [] : http.get<PostResponse[]>(`/posts/user/${userId}`).then((res) => res.data)),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(userId),
  });
}

export function usePostComments(postId?: string, opts: FunctionalUseQueryOptions<PostComment[]> = {}) {
  return useQueryLPC<PostComment[]>({
    queryKey: postKeys.comments(postId ?? ''),
    queryFn: () => (!postId ? [] : http.get<PostComment[]>(`/posts/${postId}/comments`).then((res) => res.data)),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(postId),
  });
}

// ─── Mutations ───────────────────────────────────────────────────────

export function useCreatePost(opts: UseMutationOptions<PostResponse, Error, CreatePostDTO> = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return http.post<PostResponse>('/posts', asFormData(payload)).then((res) => res.data);
    },
    ...opts,
    onSuccess(post, variables, context) {
      void client.invalidateQueries({ queryKey: postKeys.all });
      void client.invalidateQueries({ queryKey: loanKeys.lists() });
      void client.invalidateQueries({ queryKey: loanKeys.detail(post.loanId) });
      client.setQueryData(postKeys.detail(post.id), post);
      opts.onSuccess?.(post, variables, context);
    },
  });
}

export function useUpdatePost(opts: UseMutationOptions<PostResponse, Error, { id: string } & UpdatePostDTO> = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      http.patch<PostResponse>(`/posts/${id}`, asFormData(payload)).then((res) => res.data),
    ...opts,
    onSuccess(post, variables, context) {
      void client.invalidateQueries({ queryKey: postKeys.all });
      void client.invalidateQueries({ queryKey: loanKeys.detail(post.loanId) });
      client.setQueryData(postKeys.detail(post.id), post);
      opts.onSuccess?.(post, variables, context);
    },
  });
}

export function useDeletePost(opts: UseMutationOptions<{ deleted: true }, Error, PostResponse> = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => http.delete<{ deleted: true }>(`/posts/${id}`).then((res) => res.data),
    ...opts,
    onSuccess(result, post, context) {
      void client.invalidateQueries({ queryKey: postKeys.all });
      void client.invalidateQueries({ queryKey: loanKeys.detail(post.loanId) });
      return opts.onSuccess?.(result, post, context);
    },
  });
}

export function useLikePost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => http.post<{ liked: true }>(`/posts/${postId}/like`).then((res) => res.data),
    onSettled: () => void client.invalidateQueries({ queryKey: postKeys.all }),
  });
}

export function useUnlikePost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => http.delete<{ liked: false }>(`/posts/${postId}/like`).then((res) => res.data),
    onSettled: () => void client.invalidateQueries({ queryKey: postKeys.all }),
  });
}

export function useCreatePostComment(postId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => http.post<PostComment>(`/posts/${postId}/comments`, { body }).then((res) => res.data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: postKeys.comments(postId) });
      void client.invalidateQueries({ queryKey: postKeys.all });
    },
  });
}

export function useDeletePostComment(postId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => http.delete(`/posts/${postId}/comments/${commentId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: postKeys.comments(postId) });
      void client.invalidateQueries({ queryKey: postKeys.all });
    },
  });
}
